import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeTreatmentInformedConsent } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

export interface TreatmentInformedConsentFormData {
    student: string;
    formId: string;
    isPsychologicalServices: boolean;
    isPsychotherapeuticServices: boolean;
    isProcess: boolean;
    isTermination: boolean;
    fullName: string;
    clientName: string;
    todayDate: string | null;
}

interface TreatmentInformedConsentFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<TreatmentInformedConsentFormData, TreatmentInformedConsentFormProps>;

const validators: Validators<TreatmentInformedConsentFormData> = {
    isPsychologicalServices: [required({ message: "It's required." })],
    isPsychotherapeuticServices: [required({ message: "It's required." })],
    isProcess: [required({ message: "It's required." })],
    isTermination: [required({ message: "It's required." })],
    clientName: [required({ message: 'Client Full Name is required.' }), maxLength({ max: 255 })],
    fullName: [required({ message: 'Full Name is required.' }), maxLength({ max: 255 })],
    todayDate: [required({ message: 'Date is required.' }), maxLength({ max: 255 })],
};

const TreatmentInformedConsentForm: React.FunctionComponent<InjectedProps & TreatmentInformedConsentFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & TreatmentInformedConsentFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>Informed Consent: Treatment of a Minor</h2>
            <div>
                This document contains important information about our professional services and business policies. This
                consent form represents an agreement between you and Manhattan Psychology Group, PC.{' '}
            </div>
            <div>(please acknowledge by checking below)</div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={4}
                wrapperConfig={{
                    label: '',
                }}
            />

            <FieldInput
                name="clientName"
                type="text"
                validate={validators.clientName}
                wrapperConfig={{
                    label: 'Client Full Name: *',
                }}
            />
            <div className={classNames(styles.checkBoxContainer)}>
                <FieldInput
                    name="isPsychologicalServices"
                    type="checkbox"
                    validate={validators.isPsychologicalServices}
                    wrapperConfig={{
                        label: 'Initial Intake and Diagnostic Evaluation *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
                <div className={classNames(styles.checkBoxLabel)}>
                    The first session will involve an initial evaluation, during which the clinician will ask you for detailed information about yourself/child, including details about concerns, symptoms, and adaptive behaviors. You may be asked to complete formal measures to help us understand your concerns better. You may also be asked to sign consent for the clinician to contact other providers or educators who know you/your child well. Any relevant medical, treatment, or educational records will be requested. After the initial evaluation, the clinician will discuss the results of the intake and offer you some first impressions of what therapy will include and a treatment plan to follow, if you decide to continue with therapy. The clinician will discuss with you their working understanding of the problem, treatment plan, therapeutic objectives, and their view of the possible outcomes of treatment. If you have any unanswered questions about any of the procedures used in the course of therapy, the possible risks, the clinician’s expertise in employing them, or about the treatment plan, please ask them and you will be answered fully. You also have the right to ask about other treatments and their risks and benefits. You should evaluate this information along with your own opinion of whether you feel comfortable working with the clinician. Therapy involves a commitment of time and energy, so you should be very careful about finding a therapist with whom you feel comfortable. If you have any questions about the procedures of this clinic, you are encouraged to bring them up whenever they arise. If your doubts persist, we will work with your or refer you to another provider/service.
                </div>
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <FieldInput
                    name="isPsychotherapeuticServices"
                    type="checkbox"
                    validate={validators.isPsychotherapeuticServices}
                    wrapperConfig={{
                        label: 'Psychological Services *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
                <div className={classNames(styles.checkBoxLabel)}>
                    Psychotherapy is not easily described in general statements. It varies depending on the personalities of the therapist and the patient and the problems you bring forward. There are many differ methods which may be used to deal with the problems you hope to address. Specific treatment modalities, such as Parent-Child Interaction Therapy, Parent Management Training, Cognitive Behavioral Therapy, or Dialectic Behavioral Therapy, may be used when appropriate or other approaches may be suggested.
                </div>
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <FieldInput
                    name="isProcess"
                    type="checkbox"
                    validate={validators.isProcess}
                    wrapperConfig={{
                        label: 'The Process of Therapy and Scope of Practice *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
                <div className={classNames(styles.checkBoxLabel)}>
                    Psychotherapy and behavioral therapies can have benefits and risks. Since therapy often involves discussing unpleasant aspects of life, you or your child may experience uncomfortable feelings like sadness, guilt, anger, frustration, and helplessness. You may be asked to try new parenting practices that are different from those you have used before, which can feel uncomfortable at first. On the other hand, psychotherapy has been shown to have benefits for people who go through it. Therapy often leads to better relationships, resolutions to specific problems, and significant reductions in feelings of distress. Clinicians at Manhattan Psychology Group, PC are committed to providing therapy that is deemed to be most appropriate for you and your family based on the problems discussed and treatments supported by the literature. However, there are no guarantees of what you will experience. Clinicians at Manhattan Psychology Group, PC do not provide custody evaluation, recommendation, medication, or prescription recommendation nor legal advice, as these activities do not fall within their scope of practice.
                </div>
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <FieldInput
                    name="isTermination"
                    type="checkbox"
                    validate={validators.isTermination}
                    wrapperConfig={{
                        label: 'Termination *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
                <div className={classNames(styles.checkBoxLabel)}>
                    As set forth above, during the course of treatment, your clinician will assess if they can be of benefit to you. Clinicians at Manhattan Psychology Group, PC do not work with clients who, in their opinion, they cannot help or who are not benefitting from their services. In such a case, if appropriate, your clinician will give you referrals to contact. If you request and authorize it in writing, the clinician will talk to the new psychotherapist of your choice to help with the transition. You have the right to terminate therapy or communication at any time. If you choose to do so, upon your request and if it is appropriate and possible, Manhattan Psychology Group, PC will provide you with names of other qualified professionals who services you might prefer.
                </div>
            </div>

            <FieldInput
                name="fullName"
                type="text"
                validate={validators.fullName}
                wrapperConfig={{
                    label: 'Patient/Parent/Guardian Initials: *',
                }}
            />

            <FieldDate
                name="todayDate"
                type="text"
                date={true}
                validate={validators.todayDate}
                wrapperConfig={{
                    label: 'Date: *',
                }}
            />

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<TreatmentInformedConsentFormData, TreatmentInformedConsentFormProps>({
    form: 'informed-consent-for-testing-form',
    enableReinitialize: true,
    onSubmit: async (
        data: TreatmentInformedConsentFormData,
        dispatch: Dispatch<any>,
        props: TreatmentInformedConsentFormProps,
    ) => {
        props.setIsLoader(true);
        await asyncPersistIntakeTreatmentInformedConsent({ ...data }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<TreatmentInformedConsentFormData, TreatmentInformedConsentFormProps, string>,
    ) {
        props.setIsLoader(false);
        //toast.success(`The form was successfully sent.`);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        // props.setIsLoader(false);
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(TreatmentInformedConsentForm);
